/**
 * OperationHandler - централизованный обработчик операций
 * Определяет тип операции и вызывает соответствующий обработчик
 */

import { isStateTreeNode, getSnapshot } from 'mobx-state-tree';

class OperationHandler {
    constructor(rootStore) {
        this.rootStore = rootStore;
        this.uiStore = rootStore.ui;
        
        // Операции требующие модалку
        this.modalOperations = ['create', 'edit', 'update', 'delete'];
        
        // Операции выполняемые напрямую
        this.directOperations = ['reset', 'sync', 'refresh', 'export', 'import'];
        
        // Специальные операции с кастомной логикой
        this.customOperations = ['bulk-update', 'bulk-delete', 'clone'];
    }
    
    /**
     * Главный метод обработки операций
     * @param {Object} params
     * @param {string} params.operation - тип операции (create, edit, delete, etc.)
     * @param {Object} params.context - контекст страницы (entity, schema, store)
     * @param {string} params.targetId - ID целевого объекта (для edit/delete)
     * @param {Object} params.data - данные для операции
     * @param {Function} params.onSuccess - колбек после успешной операции
     * @param {Function} params.onError - колбек при ошибке
     */
    async handle({ operation, context, targetId, data, onSuccess, onError }) {
        try {
            const { entity, schema, store } = context;
            
            // Определяем паттерн операции
            if (this.modalOperations.includes(operation)) {
                return await this.handleModalOperation({ 
                    operation, entity, schema, store, targetId, data 
                });
            } else if (this.directOperations.includes(operation)) {
                return await this.handleDirectOperation({ 
                    operation, store, data 
                });
            } else if (this.customOperations.includes(operation)) {
                return await this.handleCustomOperation({ 
                    operation, entity, store, data 
                });
            } else {
                console.warn(`Unknown operation: ${operation}`);
                return false;
            }
            
        } catch (error) {
            console.error('[OperationHandler] Error:', error);
            if (onError) onError(error);
            return false;
        }
    }
    
    /**
     * Обработка операций через модалку
     */
    async handleModalOperation({ operation, entity, schema, store, targetId, data }) {
        // Автоматически преобразуем MST объекты в обычные
        const plainData = data && isStateTreeNode(data) ? getSnapshot(data) : data;

        // Просто открываем модалку, вся логика в SmartModalContainer
        this.uiStore.openModal({
            operation: operation === 'update' ? 'edit' : operation,
            entityName: entity,  // UIStore ожидает entityName, не entity
            targetId: targetId || plainData?.id,
            data: plainData || null
        });

        return true;
    }
    
    /**
     * Обработка прямых операций без модалки
     */
    async handleDirectOperation({ operation, store, data }) {
        switch (operation) {
            case 'reset':
                if (store.reset) {
                    await store.reset();
                } else if (store.executeSpecial) {
                    await store.executeSpecial('reset');
                }
                break;
                
            case 'sync':
                if (store.sync) {
                    await store.sync();
                } else if (store.executeSpecial) {
                    await store.executeSpecial('sync');
                }
                break;
                
            case 'refresh':
                await store.load();
                break;
                
            case 'export':
                // TODO: Implement export logic
                console.log('Export operation not implemented yet');
                break;
                
            case 'import':
                // TODO: Implement import logic
                console.log('Import operation not implemented yet');
                break;
                
            default:
                console.warn(`Direct operation ${operation} not implemented`);
        }
        
        return true;
    }
    
    /**
     * Обработка кастомных операций
     */
    async handleCustomOperation({ operation, entity, store, data }) {
        switch (operation) {
            case 'bulk-update':
                // Открываем специальную модалку для массового обновления
                const bulkData = data || store.selectedItems;
                const plainBulkData = bulkData && isStateTreeNode(bulkData) ? getSnapshot(bulkData) : bulkData;
                this.uiStore.openModal({
                    operation: 'bulk-update',
                    entity,
                    data: plainBulkData
                });
                break;
                
            case 'bulk-delete':
                // Открываем модалку подтверждения массового удаления
                const deleteData = data || store.selectedItems;
                const plainDeleteData = deleteData && isStateTreeNode(deleteData) ? getSnapshot(deleteData) : deleteData;
                this.uiStore.openModal({
                    operation: 'bulk-delete',
                    entity,
                    data: plainDeleteData
                });
                break;
                
            case 'clone':
                // Клонирование объекта
                if (data) {
                    const clonedData = { ...data };
                    delete clonedData.id;
                    clonedData.name = `${clonedData.name || 'Item'} (копия)`;
                    
                    const plainClonedData = clonedData && isStateTreeNode(clonedData) ? getSnapshot(clonedData) : clonedData;
                    this.uiStore.openModal({
                        operation: 'create',
                        entity,
                        data: plainClonedData
                    });
                }
                break;
                
            default:
                console.warn(`Custom operation ${operation} not implemented`);
        }
        
        return true;
    }
    
    /**
     * Проверка доступности операции
     */
    canExecute(operation, permissions) {
        const operationMap = {
            'create': 'canCreate',
            'edit': 'canUpdate',
            'update': 'canUpdate',
            'delete': 'canDelete',
            'reset': 'hasSpecialOperations',
            'sync': 'hasSpecialOperations'
        };
        
        const permission = operationMap[operation];
        if (!permission) return true; // Неизвестные операции разрешены по умолчанию
        
        return permissions[permission] || false;
    }
}

// Singleton instance
let instance = null;

export const initOperationHandler = (rootStore) => {
    if (!instance) {
        instance = new OperationHandler(rootStore);
    }
    return instance;
};

export const getOperationHandler = () => {
    if (!instance) {
        console.error('[OperationHandler] Not initialized. Call initOperationHandler first.');
    }
    return instance;
};

export default OperationHandler;